import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  loadClientSettings,
  loadPersistedLanguage,
  saveClientSettings,
} from "../client/dashboard/clientPreferencesStorage";
import de from "./locales/de";
import en from "./locales/en";
import deExt from "./locales/de-ext";
import enExt from "./locales/en-ext";

export const SUPPORTED_LOCALES = ["de", "en"];
export const DEFAULT_LOCALE = "de";
const APP_LOCALE_KEY = "repman-app-locale";

function deepMerge(target, source) {
  const out = { ...target };
  for (const key of Object.keys(source)) {
    if (
      source[key] &&
      typeof source[key] === "object" &&
      !Array.isArray(source[key]) &&
      target[key] &&
      typeof target[key] === "object"
    ) {
      out[key] = deepMerge(target[key], source[key]);
    } else {
      out[key] = source[key];
    }
  }
  return out;
}

const MESSAGES = {
  de: deepMerge(deepMerge(de, deExt), {}),
  en: deepMerge(deepMerge(en, enExt), {}),
};

function normalizeLocale(value) {
  if (value === "en" || value === "de") return value;
  return DEFAULT_LOCALE;
}

function getByPath(obj, path) {
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
}

function interpolate(template, vars = {}) {
  if (typeof template !== "string") return String(template ?? "");
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) =>
    vars[key] != null ? String(vars[key]) : ""
  );
}

export function resolveInitialLocale() {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  return normalizeLocale(loadPersistedLanguage() || DEFAULT_LOCALE);
}

export function persistLocale(locale) {
  const safe = normalizeLocale(locale);
  if (typeof window === "undefined") return safe;

  window.localStorage.setItem(APP_LOCALE_KEY, safe);
  document.documentElement.lang = safe;

  try {
    const settings = loadClientSettings();
    if (settings.language !== safe) {
      saveClientSettings({ ...settings, language: safe });
    }
  } catch {
    /* ignore before login */
  }

  return safe;
}

const I18nContext = createContext({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  t: (key) => key,
});

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(resolveInitialLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  // Re-apply saved language after login when company-scoped settings become available.
  useEffect(() => {
    const syncFromStorage = () => {
      const next = resolveInitialLocale();
      setLocaleState((current) => (current === next ? current : next));
    };

    syncFromStorage();
    window.addEventListener("repman:session-changed", syncFromStorage);
    return () => window.removeEventListener("repman:session-changed", syncFromStorage);
  }, []);

  const setLocale = useCallback((next) => {
    const safe = persistLocale(next);
    setLocaleState(safe);
  }, []);

  const t = useCallback(
    (key, vars) => {
      const pack = MESSAGES[locale] || MESSAGES[DEFAULT_LOCALE];
      const fallback = MESSAGES[DEFAULT_LOCALE];
      const raw =
        getByPath(pack, key) ?? getByPath(fallback, key) ?? key;
      return interpolate(raw, vars);
    },
    [locale]
  );

  const value = useMemo(
    () => ({ locale, setLocale, t }),
    [locale, setLocale, t]
  );

  return (
    <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
  );
}

export function useTranslation() {
  return useContext(I18nContext);
}

export default I18nProvider;
